import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useUpdateOrder } from '../hooks/use-order-mutations';
import {
  contactSchema,
  fromOrderContact,
  toContactPatch,
  type ContactForm,
} from '../schemas/order-edit-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['email', 'phone', 'note']);

export interface EditContactDialogProps {
  storeId: string;
  order: Order;
  onOpenChange: (open: boolean) => void;
}

/** The order's contact details and the shopper's note; the timeline records the change. */
export function EditContactDialog({ storeId, order, onOpenChange }: EditContactDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const update = useUpdateOrder(storeId, 'orders:toasts.contactUpdated');
  const form = useForm<ContactForm>({
    resolver: useI18nResolver(contactSchema),
    defaultValues: fromOrderContact(order),
  });

  const submit = form.handleSubmit(async (values) => {
    const body = toContactPatch(values, order);
    if (Object.keys(body).length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await update.mutateAsync({ id: order.id, body });
      onOpenChange(false);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:editContact.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:editContact.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:customer.email')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>{t('orders:editContact.emailHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:customer.phone')}</FormLabel>
                  <FormControl>
                    <Input type="tel" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:detail.customerNote')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormDescription>{t('orders:editContact.noteHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
